// frontend/src/components/meetings/MeetingScheduler.jsx
import React, { useState } from "react";
import { X, Calendar, Clock, Link, Video } from "lucide-react";
import api from "../../services/api";
import { toast } from "react-toastify";

const MeetingScheduler = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    platform: "zoom",
    meetingUrl: "",
    passcode: "",
    title: "",
    scheduledTime: "",
    expectedDuration: 60,
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.scheduleMeeting(formData);
      toast.success("Meeting scheduled successfully!");
      onSuccess(response.meeting);
      onClose();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to schedule meeting"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-[#0C0E12] rounded-xl p-6 w-full max-w-2xl border border-gray-700">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-white">
            Schedule Meeting Recording
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Platform Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Platform
            </label>
            <div className="grid grid-cols-3 gap-3">
              {[
                { id: "zoom", name: "Zoom", icon: "/assets/zoom.png" },
                { id: "teams", name: "Teams", icon: "/assets/teams.png" },
                { id: "meet", name: "Google Meet", icon: "/assets/meets.png" },
              ].map((platform) => (
                <button
                  key={platform.id}
                  type="button"
                  onClick={() =>
                    setFormData((prev) => ({ ...prev, platform: platform.id }))
                  }
                  className={`p-4 rounded-lg border-2 transition-all ${
                    formData.platform === platform.id
                      ? "border-purple-500 bg-purple-500/20"
                      : "border-gray-600 hover:border-gray-500"
                  }`}
                >
                  <img
                    src={platform.icon}
                    alt={platform.name}
                    className="w-8 h-8 mx-auto mb-2"
                  />
                  <span className="text-white text-sm">{platform.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Meeting Title */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Meeting Title
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className="w-full px-3 py-2 bg-[#1a1d23] border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500"
              placeholder="e.g., Weekly Team Standup"
              required
            />
          </div>

          {/* Meeting URL */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Meeting URL
            </label>
            <div className="relative">
              <Link className="absolute left-3 top-3 text-gray-400" size={20} />
              <input
                type="url"
                name="meetingUrl"
                value={formData.meetingUrl}
                onChange={handleChange}
                className="w-full pl-10 pr-3 py-2 bg-[#1a1d23] border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500"
                placeholder={
                  formData.platform === "zoom"
                    ? "https://zoom.us/j/123456789"
                    : formData.platform === "teams"
                    ? "https://teams.microsoft.com/l/meetup-join/..."
                    : "https://meet.google.com/xxx-xxxx-xxx"
                }
                required
              />
            </div>
          </div>

          {/* Passcode (only for Zoom) */}
          {formData.platform === "zoom" && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Passcode (optional)
              </label>
              <input
                type="text"
                name="passcode"
                value={formData.passcode}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-[#1a1d23] border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500"
                placeholder="Meeting passcode"
              />
            </div>
          )}

          {/* Schedule Time and Duration */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                <Calendar className="inline w-4 h-4 mr-1" />
                Date & Time
              </label>
              <input
                type="datetime-local"
                name="scheduledTime"
                value={formData.scheduledTime}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-[#1a1d23] border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                <Clock className="inline w-4 h-4 mr-1" />
                Expected Duration (minutes)
              </label>
              <input
                type="number"
                name="expectedDuration"
                value={formData.expectedDuration}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-[#1a1d23] border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500"
                min="15"
                max="240"
                step="15"
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors disabled:opacity-50"
              disabled={loading}
            >
              {loading ? "Scheduling..." : "Schedule Recording"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MeetingScheduler;
